"use client";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { loyaltyBenefitIconNames } from "@jti/ui/components/CartOverviewTier";

import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";

import { sobQuizInputs } from "../SobQuiz/SobQuiz.builder";

export const checkoutInputs = [
  {
    name: "loyalty",
    type: "object",
    defaultValue: {
      learnMoreUrl: "/",
      orderConfirmationLoyaltyUrl: "/",
      redemptionTabSymbol: "$",
      showMgm: false,
      showReminderDialog: false,
    },
    subFields: [
      {
        name: "learnMoreUrl",
        type: "url",
      },
      {
        name: "orderConfirmationLoyaltyUrl",
        type: "url",
        friendlyName: "Order confirmation - loyalty component arrow link",
      },
      {
        name: "redemptionTabSymbol",
        type: "string",
      },
      {
        name: "showMgm",
        friendlyName: "Show MGM in Cart",
        type: "boolean",
      },
      {
        name: "showReminderDialog",
        friendlyName: "Show or hide reminder dialog",
        type: "boolean",
        required: true,
      },
      {
        name: "reminderDialogSKUs",
        friendlyName: "SKUs that trigger reminder, comma seperated",
        type: "string",
        required: false,
        defaultValue: "",
      },
      {
        name: "reminderDialogTitle",
        type: "string",
        required: true,
        defaultValue: "Title",
      },
      {
        name: "reminderDialogSubtitle",
        type: "string",
        required: true,
        defaultValue: "Subtitle",
      },
      {
        name: "reminderDialogText",
        type: "string",
        required: true,
        defaultValue: "Text",
      },
      {
        name: "reminderDialogSecondaryButtonText",
        type: "string",
        required: true,
        defaultValue: "Secondary Button Text",
      },
      {
        name: "reminderDialogSecondaryButtonLink",
        type: "url",
        required: true,
        defaultValue: "Secondary Button Link",
      },
    ],
  },
  {
    name: "cartOverviewTierV2Enabled",
    type: "boolean",
    friendlyName: "Enable Cart Overview Tier (V2)",
    defaultValue: false,
  },
  {
    name: "cartOverviewTier",
    type: "object",
    friendlyName: "Cart Overview Tier (V2)",
    showIf: "options.get('cartOverviewTierV2Enabled') === true",
    defaultValue: {
      pointsLabel: "Points",
      collectMessage: "Collect {purchasePoints} points on this order!",
      knowMoreLabel: "Know more",
      statusMessages: {
        statusMessageUpgrade: "You will be promoted to the next tier after this purchase",
        statusMessageRetention: "Collect more points to reach the next tier",
        statusMessageDowngrade: "Your points will reset at the end of the year",
        statusMessageDowngradeRetained: "You will maintain your tier with this purchase",
        statusMessageMax: "You have reached the highest tier",
      },
      showBenefits: false,
    },
    subFields: [
      {
        name: "title",
        type: "string",
        friendlyName: "Container title",
      },
      {
        name: "descriptionCollapsed",
        type: "string",
        friendlyName: "Container description (collapsed)",
      },
      {
        name: "descriptionExpanded",
        type: "string",
        friendlyName: "Container description (expanded)",
      },
      {
        name: "pointsLabel",
        type: "string",
        defaultValue: "Points",
      },
      {
        name: "collectMessage",
        type: "string",
        helperText: "Use {purchasePoints} as a placeholder for the points earned on this order",
        defaultValue: "Collect {purchasePoints} points on this order!",
      },
      {
        name: "knowMoreLabel",
        type: "string",
        defaultValue: "Know more",
      },
      {
        name: "knowMoreUrl",
        type: "url",
      },
      {
        name: "statusMessages",
        type: "object",
        friendlyName: "Status messages",
        helperText:
          "Use {purchasePoints}, {pointsToNextTier}, {pointsToNextTierWithPurchase}, {pointsToMaintainCurrentTier}, {pointsToMaintainCurrentTierWithPurchase}, and {currentTier}, {nextTier} as placeholders",
        subFields: [
          {
            name: "statusMessageUpgrade",
            type: "string",
            friendlyName: "Status message - Tier upgrade",
            required: true,
            defaultValue:
              "Just {pointsToNextTierWithPurchase} points away from free shipping on every order and {nextTier} benefits",
          },
          {
            name: "statusMessageRetention",
            type: "string",
            friendlyName: "Status message - Tier retention",
            required: true,
            defaultValue: "Collect more points to reach the next tier",
          },
          {
            name: "statusMessageDowngrade",
            type: "string",
            friendlyName: "Status message - Tier downgrade",
            required: true,
            defaultValue: "Your points will reset at the end of the year",
          },
          {
            name: "statusMessageDowngradeRetained",
            type: "string",
            friendlyName: "Status message - Tier downgrade retained",
            required: true,
            defaultValue: "You have enough points with the purchase to maintain your current tier.",
          },
          {
            name: "statusMessageMax",
            type: "string",
            friendlyName: "Status message - Maximum tier",
            required: true,
            defaultValue: "You have reached the highest tier",
          },
        ],
      },
      {
        name: "showBenefits",
        type: "boolean",
        friendlyName: "Show benefits section",
        defaultValue: false,
      },
      {
        name: "goldBenefits",
        type: "object",
        friendlyName: "Gold benefits",
        defaultValue: { title: "Gold benefits" },
        subFields: [
          {
            name: "title",
            type: "string",
            defaultValue: "Gold benefits",
          },
          {
            name: "items",
            type: "list",
            copyOnAdd: false,
            helperText: `Minimum 1, maximum 4 benefits`,
            subFields: [
              {
                name: "icon",
                type: "string",
                enum: loyaltyBenefitIconNames,
                required: true,
              },
              {
                name: "text",
                type: "string",
                required: true,
              },
            ],
          },
        ],
      },
      {
        name: "blackBenefits",
        type: "object",
        friendlyName: "Black benefits",
        defaultValue: { title: "Black benefits" },
        subFields: [
          {
            name: "title",
            type: "string",
            defaultValue: "Black benefits",
          },
          {
            name: "items",
            type: "list",
            copyOnAdd: false,
            helperText: `Minimum 1, maximum 4 benefits`,
            subFields: [
              {
                name: "icon",
                type: "string",
                enum: loyaltyBenefitIconNames,
                required: true,
              },
              {
                name: "text",
                type: "string",
                required: true,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    name: "deliveryOptions",
    type: "list",
    defaultValue: [
      {
        label: "Nova Poshta",
        widgetTagName: "nova-poshta-widget",
        widgetUrl:
          "https://custom-delivery-widget-des-aem-playground-e38bb9ef65cf215a050ef.gitlab-pages.jtides.com/nova-poshta-iframe.js",
        widgetVariant: "modal",
        openingHoursEnabled: false,
        storeCardAddressEnabled: false,
      },
    ],
    subFields: [
      {
        name: "defaultDelivery",
        type: "boolean",
      },
      {
        name: "enableStoreFilteringByAttributes",
        type: "string",
        enum: ["", "flavor_label", "color_label", "both"],
      },
      {
        name: "label",
        type: "string",
        required: true,
      },
      {
        name: "methodCode",
        type: "string",
        required: true,
      },
      {
        name: "widgetTagName",
        type: "string",
        required: false,
      },
      {
        name: "widgetUrl",
        type: "string",
        required: false,
      },
      {
        name: "widgetVariant",
        type: "string",
        enum: ["embedded", "modal"],
        required: false,
      },
      {
        name: "lat",
        type: "string",
      },
      {
        name: "lon",
        type: "string",
      },
      {
        name: "storeLocatorShopDetailsPage",
        type: "url",
        required: false,
      },
      {
        name: "flotiqContentType",
        type: "string",
        required: false,
      },
      {
        name: "flotiqApiKey",
        type: "string",
        required: false,
      },
      {
        name: "description",
        type: "richText",
      },
      {
        name: "openingHoursEnabled",
        type: "boolean",
        friendlyName: "Show shop opening hours",
      },
      {
        name: "openingHoursBreaksEnabled",
        type: "boolean",
        friendlyName: "Show shop opening hours with breaks",
      },
      {
        name: "availableSticksEnabled",
        type: "boolean",
        friendlyName: "Show available stick flavours",
      },
      {
        name: "availableDevicesEnabled",
        type: "boolean",
        friendlyName: "Show available device colors",
      },
      {
        name: "filtersEnabled",
        type: "boolean",
        friendlyName: "Show filters",
      },
      {
        name: "storeCardAddressEnabled",
        type: "boolean",
        friendlyName: "Show store card address (mobile)",
      },
      {
        name: "defaultZoom",
        type: "string",
        friendlyName: "Map default zoom level",
        helperText: "Initial zoom level for the store locator map",
        defaultValue: "15",
      },
    ],
  },
  {
    name: "payOnDelivery",
    type: "object",
    defaultValue: {
      paymentMethod:
        "You have chosen payment on delivery, please make sure to follow up with delivery and have the exact amount when you receive the product.",
    },
    subFields: [
      {
        name: "paymentMethod",
        type: "richText",
      },
    ],
  },
  {
    name: "zeroValueOrder",
    type: "object",
    defaultValue: {
      userNotification: "Your order is free of charge. You will receive an email confirmation shortly.",
    },
    subFields: [
      {
        name: "userNotification",
        type: "richText",
      },
    ],
  },
  {
    name: "taxInfo",
    friendlyName: "Prices block",
    type: "object",
    defaultValue: {
      message: "The price includes 23% VAT.",
      visible: true,
    },
    subFields: [
      {
        name: "message",
        type: "richText",
        friendlyName: "Additional tax information",
      },
      {
        name: "visible",
        type: "boolean",
        friendlyName: "Is additional tax information visible?",
      },
    ],
  },
  {
    name: "failedPaymentFlow",
    type: "object",
    defaultValue: {
      title: "Payment Failed",
      message: "Your payment has failed. Please try again or contact us for further assistance.",
      retryPaymentButtonLabel: "Retry Payment",
    },
    subFields: [
      {
        name: "title",
        type: "string",
      },
      {
        name: "message",
        friendlyName: "Failed payment notification message",
        type: "richText",
      },
      {
        name: "retryPaymentButtonLabel",
        type: "string",
        required: true,
      },
    ],
  },
  {
    name: "contactConfig",
    type: "object",
    subFields: [
      {
        name: "differentRecipientDisabled",
        type: "boolean",
        friendlyName: "Is different recipient in Contact disabled?",
      },
      {
        name: "populatedPhoneInput",
        type: "boolean",
        friendlyName: "Is populated phone input enabled?",
      },
    ],
  },
  {
    name: "consentsConfig",
    type: "object",
    subFields: [
      {
        name: "policyAndTermsConsentDisabled",
        type: "boolean",
        friendlyName: "Does the payment provider have its own policy and terms checkbox?",
      },
    ],
  },
  {
    name: "discountDisclaimer",
    type: "object",
    defaultValue: {
      enabled: false,
      enabledWhenZeroValueDeposit: false,
      disclaimerMessage:
        "Promo codes and loyalty points can not be applied to tobacco products. To learn more please read out terms and conditions.",
      warningMessage: "Minimum payable amount for your current cart is {0}",
    },
    subFields: [
      {
        name: "enabled",
        friendlyName: "Show 'no discount' disclaimer for selected products",
        type: "boolean",
      },
      {
        name: "enabledWhenZeroValueDeposit",
        friendlyName:
          "Show 'No Discount' disclaimer even when the deposit amount is zero and the order is part of a free trial",
        type: "boolean",
      },
      {
        name: "disclaimerMessage",
        type: "richText",
      },
      {
        name: "warningMessage",
        type: "richText",
        friendlyName: "Minimum Payable Amount Warning Message",
      },
    ],
  },
  {
    name: "ageVerificationPopup",
    type: "object",
    subFields: [
      {
        name: "description",
        type: "richText",
        friendlyName: "Description of age verification popup",
      },
      {
        name: "successMessage",
        type: "richText",
        friendlyName: "Success message after successfull age verification",
      },
      {
        name: "popupMode",
        type: "boolean",
        friendlyName: "Display age verification banner in popup mode",
      },
    ],
  },
  {
    name: "consentReminderPopup",
    type: "object",
    defaultValue: {
      consentReminderPopupEnabled: false,
      mainImage: "",
      imageAltText: "alt text",
      subtitle: "Great choice! Your order is confirmed.",
      mainQuestion: "Want to get the latest Offers and Product Information about EVO and Ploom?",
      description:
        "If you agree, a new tab will open to confirm your preferences while we take you to your order confirmation page.",
      yesButtonText: "Yes, keep me updated",
      noButtonText: "Not now",
      yesRedirectUrl: "https://www.google.com",
      maximumOccurancesPerYear: 5,
      consentExpirationLimit: 60,
      popUpDelay: 3,
      offerConsentName: "offers_consent",
      productInformationConsentName: "product_information_consent",
      ploomClubConsentName: "ploom_club_consent",
    },
    subFields: [
      {
        name: "consentReminderPopupEnabled",
        type: "boolean",
        required: true,
      },
      {
        name: "mainImage",
        required: true,
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "imageAltText",
        type: "string",
        required: true,
      },
      {
        name: "subtitle",
        type: "string",
        required: true,
      },
      {
        name: "mainQuestion",
        type: "string",
        required: true,
      },
      {
        name: "description",
        type: "string",
        required: true,
      },
      {
        name: "yesButtonText",
        type: "string",
        required: true,
      },
      {
        name: "noButtonText",
        type: "string",
        required: true,
      },
      {
        name: "yesRedirectUrl",
        type: "url",
        required: true,
      },
      {
        name: "maximumOccurancesPerYear",
        type: "number",
        defaultValue: 5,
        friendlyName: "Maximum occurrences per year",
        required: true,
      },
      {
        name: "consentExpirationLimit",
        type: "number",
        defaultValue: 60,
        friendlyName: "Consent expiration limit (days)",
        required: true,
      },
      {
        name: "popUpDelay",
        type: "number",
        defaultValue: 3,
        friendlyName: "Pop-up delay (seconds)",
        required: true,
      },
      {
        name: "offerConsentName",
        type: "string",
        defaultValue: "offers_consent",
        friendlyName: "Offer consent name",
        required: true,
      },
      {
        name: "productInformationConsentName",
        type: "string",
        defaultValue: "product_information_consent",
        friendlyName: "Product information consent name",
        required: true,
      },
      {
        name: "ploomClubConsentName",
        type: "string",
        defaultValue: "ploom_club_consent",
        friendlyName: "Ploom Club consent name",
        required: true,
      },
    ],
  },
  {
    name: "orderSummaryConfig",
    type: "object",
    required: true,
    subFields: [
      {
        name: "displayItemsSummary",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "displayShippingAddress",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "displayDeliveryMethod",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "displayBillingAddress",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "displayPaymentMethod",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "displayLoyaltyConfirmation",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "displayAdditionalInfo",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "additionalInfo",
        type: "object",
        subFields: [
          {
            name: "title",
            type: "string",
          },
          {
            name: "message",
            type: "string",
          },
        ],
      },
      {
        name: "withdrawOrderInfo",
        type: "richText",
        defaultValue: "",
      },
      {
        name: "displaySocialMedia",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "socialMediaTitle",
        type: "string",
        defaultValue: "",
      },
      {
        name: "socialMedia",
        type: "list",
        defaultValue: [],
        subFields: [
          {
            name: "icon",
            type: "string",
            enum: [
              "FACEBOOK",
              "INSTAGRAM",
              "LINE",
              "MAIL",
              "SNAPCHAT",
              "TELEGRAM",
              "VIBER",
              "WHATS_UP",
              "X",
              "YOUTUBE",
            ],
            required: true,
          },
          {
            name: "uri",
            type: "string",
            required: true,
          },
        ],
      },
    ],
  },
  {
    name: "survey",
    type: "object",
    subFields: [
      {
        name: "identifier",
        type: "string",
        friendlyName: "Survey Identifier",
      },
      {
        name: "domain",
        type: "string",
        friendlyName: "Survey Domain",
      },
    ],
  },
  {
    name: "subscriptionTermsPlacement",
    type: "string",
    friendlyName: "Subscription terms placement",
    enum: ["sidebar", "consentAndPaySection"],
  },
  {
    name: "enableSobQuiz",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Enable Source of Business Quiz",
  },
  {
    name: "sob",
    type: "object",
    showIf: `options.get('enableSobQuiz') === true`,
    defaultValue: {
      uuid: "",
      type: "SOURCE_OF_BUSINESS",
      questionScreen: {
        title: "Question Screen",
        description: "Question Screen description",
      },
      consentCaptureQuestion: {
        marketingMailConsentId: "",
        title: "We'd love to send you some simple guidance to help you get familiar with Ploom.",
        description: "You'll receive how-to guides, tips, and information about Ploom Club rewards.",
      },
      finalScreen: {
        title: "Tell us about yourself",
        description:
          "This will help improve your Ploom experience and ensure that you only receive content that's most relevant to you.",
      },
    },
    subFields: sobQuizInputs,
  },
] as const satisfies Inputs;

export const CheckoutComponent: RegisteredComponent = {
  name: "Checkout",
  component: dynamic(async () => (await import("./Checkout")).Checkout, {
    ssr: false,
  }),

  inputs: checkoutInputs,
};
